import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Space, Button, Spinner, Row } from "@masterteam/mt-design-react";

import AdminPage from "../../AdminPage";
import AdminSearch from "../../AdminSearch";
import Table from "../../NewTable";
import AsyncImg from "../../AsyncImg";
import AddUser from "./AddUser";
import DeleteModal from "../../DeleteModal";
import ReplaceUser from "./ReplaceUser";
import Pagination from "../../Pagination";

import UserAvater from "../../../assets/images/user_avater.png";
import { ReactComponent as ReplaceUserIcon } from "../../../assets/images/replace-user.svg";
import { ReactComponent as PlusIcon } from "../../../assets/images/plus-symbol.svg";
import { ReactComponent as EditIcon } from "../../../assets/images/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete.svg";

import api from "../../../providers/api";
import { addToast } from "../../../helpers";

import "./style.scss";
import { set } from "lodash";

const initialModalState = {
  isOpen: false,
  data: null
};

function Users() {
  const intl = useIntl();
  const [searchedFilter, setSearchedFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userModal, setUserModal] = useState(initialModalState);
  const [replaceModal, setReplaceModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(initialModalState);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  function getAllUsers() {
    setLoading(true);
    api.user
      .getCAUser({ pageNumber: currentPage, pageSize: 100 })
      .then((res) => {
        const { StatusCode, Data } = res;
        if (StatusCode === "Success") {
          setLoading(false);
          setData(Data);
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error"
        });
      });
  }

  function selectUser(user, action) {
    switch (action) {
      case "delete":
        setDeleteModal({
          isOpen: true,
          data: user.UserName
        });
        break;

      case "edit":
        setUserModal({
          isOpen: true,
          data: user
        });
        break;

      default:
        break;
    }
  }

  function deleteUser(id) {
    setDeleteLoading(true);
    api.user
      .delete({
        UserName: id
      })
      .then((res) => {
        const { StatusCode } = res;
        if (StatusCode === "Success") {
          addToast.success("user-management.user-deleted-successfuly");
          setDeleteModal({
            isOpen: false,
            data: null
          });
          setData((currentData) => ({
            ...currentData,
            Items: currentData.Items.filter((item) => item.UserName !== id),
            TotalCount: currentData.TotalCount - 1
          }));
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "delete-user-error"
        });
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  }

  useEffect(() => {
    getAllUsers();
  }, [currentPage]);

  function showAddUserModal() {
    setUserModal({
      isOpen: true,
      data: null
    });
  }

  function showReplaceModal() {
    setReplaceModal(true);
  }

  function updateUsersList(Data, isEdit) {
    if (Data) {
      let _users = [...data?.Items];

      if (!isEdit) {
        data?.Items.push(Data);
      }

      setUserModal(initialModalState);
      setLoading(false);
      setData({ ...data, Items: _users });
    } else {
      setUserModal(initialModalState);
      setLoading(false);
    }
  }

  function closeReplaceUserModal(username) {
    setReplaceModal(false);
    setData((currentData) => ({
      ...currentData,
      Items: currentData.Items.filter((item) => item.UserName !== username),
      TotalCount: currentData.TotalCount - 1
    }));
  }

  const columns = [
    {
      key: "title",
      cell: (item) => (
        <AsyncImg
          fallback={UserAvater}
          user={item}
          alt={item.DisplayName}
          style={{ height: 30, width: 30, borderRadius: 15 }}
          // className="user-photo"
        />
      ),
      header: intl.formatMessage({ id: "user.photo" }),
      width: "70px"
    },
    {
      key: "username",
      cell: "DisplayName",
      header: intl.formatMessage({ id: "user.user-name" }),
      width: "20%"
    },
    {
      key: "mobile",
      cell: "PhoneNumber",
      header: intl.formatMessage({ id: "user.mobile" }),
      width: "25%"
    },
    {
      key: "email",
      cell: "Email",
      header: intl.formatMessage({ id: "user.email" }),
      width: "calc(45% - 70px)"
    }
  ];

  const actions = [
    {
      key: "edit",
      variant: "primary",
      Icon: EditIcon,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.update" }),
      onClick: (item) => {
        selectUser(item, "edit");
      }
    },
    {
      key: "delete",
      variant: "error",
      Icon: DeleteIcon,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.delete" }),
      onClick: (item) => {
        setDeleteModal({
          isOpen: true,
          data: item
        });
      },
      hidden: (item) => !item.IsDeletable,
      disabled: (item) => !item.IsDeletable
    }
  ];

  return (
    <AdminPage title={intl.formatMessage({ id: "common.users" })}>
      <div className="users-container">
        <div className="animated fadeInLeft">
          <div className="users-header">
            <Space align="center">
              <Button
                onClick={showAddUserModal}
                Icon={PlusIcon}
                disabled={loading}
              >
                {intl.formatMessage({ id: "common.add" })}
              </Button>
              <Button
                onClick={showReplaceModal}
                Icon={ReplaceUserIcon}
                disabled={loading}
              >
                {intl.formatMessage({ id: "user.replace-user" })}
              </Button>
            </Space>
            <div className="d-flex search-users-container">
              <span className="users-count-label">
                {intl.formatMessage({ id: "common.users" })}:&nbsp;
              </span>
              <span
                className="users-count"
                style={{ color: "var(--primary-color)" }}
              >
                {data?.TotalCount}
              </span>
              <AdminSearch
                onChange={(event) => {
                  setSearchedFilter(event);
                }}
              />
            </div>
          </div>
          {userModal.isOpen ? (
            <AddUser
              showModal={userModal.isOpen}
              editMode={userModal.data}
              selectedUser={userModal.data}
              callback={updateUsersList}
              closeHandler={() => setUserModal(initialModalState)}
            />
          ) : null}
          {replaceModal ? (
            <ReplaceUser
              showModal={replaceModal}
              closeHandler={closeReplaceUserModal}
              usersList={data?.Items}
            />
          ) : null}

          <Space
            direction="vertical"
            size="large"
            className="table-pagination-wrapper"
          >
            <Pagination
              totalPages={data?.TotalPages || 1}
              currentPage={currentPage || 1}
              setCurrentPage={setCurrentPage}
            />

            {loading ? (
              <Row justify="center" align="center">
                <Spinner size="large" />
              </Row>
            ) : (
              <Table
                data={data?.Items.filter((user) =>
                  user.DisplayName?.toLowerCase().includes(
                    searchedFilter?.trim().toLowerCase()
                  )
                )}
                columns={columns}
                actions={actions}
                actionsHeader={intl.formatMessage({ id: "common.operations" })}
              />
            )}
          </Space>
        </div>
      </div>
      {deleteModal.isOpen ? (
        <DeleteModal
          isOpen={deleteModal.isOpen}
          title={intl.formatMessage({ id: "common.user" })}
          closeModal={() =>
            setDeleteModal({
              isOpen: false,
              data: null
            })
          }
          handleDelete={() => deleteUser(deleteModal.data?.UserName)}
          ButtonStatus={deleteLoading}
        />
      ) : null}
    </AdminPage>
  );
}

export default Users;
